import React, { useEffect, useRef, useState } from "react";

const PING_URL = "https://api.checkmark.mx/ping/babilonia";
const SPEED_TEST_URL = "https://api.checkmark.mx/speed-test/download";

const PING_INTERVAL_MS = 10000;
const SPEED_INTERVAL_MS = 60000;
const SPEED_TEST_BYTES = 1024 * 1024; // 1 MB

const InternetStatus = () => {
  const [status, setStatus] = useState("Verificando conexión...");
  const [latency, setLatency] = useState(null);
  const [speedMbps, setSpeedMbps] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isCheckingSpeed, setIsCheckingSpeed] = useState(false);

  const checkingRef = useRef(false);
  const speedRef = useRef(null);
  const failCountRef = useRef(0);

  const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const getStatusByValues = (ms, mbps = null) => {
    if (mbps !== null) {
      if (ms > 1500 || mbps < 1) return "Señal muy débil";
      if (ms > 800 || mbps < 3) return "Conexión lenta";
      return "Conexión estable";
    }

    if (ms < 300) return "Conexión estable";
    if (ms < 1000) return "Conexión lenta";
    return "Señal muy débil";
  };

  const checkLatency = async () => {
    const start = performance.now();

    const response = await fetchWithTimeout(
      `${PING_URL}?t=${Date.now()}`,
      {
        method: "GET",
        cache: "no-store",
      },
      8000,
    );

    if (!response.ok) {
      throw new Error("Error en respuesta del servidor");
    }

    const end = performance.now();

    return Math.round(end - start);
  };

  const checkDownloadSpeed = async () => {
    const url = `${SPEED_TEST_URL}?bytes=${SPEED_TEST_BYTES}&t=${Date.now()}`;

    const start = performance.now();

    const response = await fetchWithTimeout(
      url,
      {
        method: "GET",
        cache: "no-store",
      },
      15000,
    );

    if (!response.ok) {
      throw new Error("Error en prueba de velocidad");
    }

    const blob = await response.blob();

    const end = performance.now();
    const seconds = (end - start) / 1000;

    if (seconds <= 0) {
      return null;
    }

    const downloadedBytes = blob.size || SPEED_TEST_BYTES;
    const mbps = (downloadedBytes * 8) / seconds / 1000000;

    return Number(mbps.toFixed(2));
  };

  const checkConnection = async (withSpeed = false) => {
    if (checkingRef.current) return;

    if (!navigator.onLine) {
      failCountRef.current = 0;
      setIsOnline(false);
      setStatus("Sin conexión");
      setLatency(null);
      setSpeedMbps(null);
      speedRef.current = null;
      return;
    }

    checkingRef.current = true;

    try {
      const ms = await checkLatency();

      failCountRef.current = 0;

      setIsOnline(true);
      setLatency(ms);

      if (withSpeed) {
        setIsCheckingSpeed(true);

        const mbps = await checkDownloadSpeed();

        if (mbps !== null) {
          speedRef.current = mbps;
          setSpeedMbps(mbps);
          setStatus(getStatusByValues(ms, mbps));
        } else {
          setStatus(getStatusByValues(ms, speedRef.current));
        }
      } else {
        setStatus(getStatusByValues(ms, speedRef.current));
      }
    } catch (error) {
      failCountRef.current += 1;

      if (failCountRef.current >= 2) {
        setIsOnline(false);
        setStatus("Sin conexión con la API");
        setLatency(null);
        setSpeedMbps(null);
        speedRef.current = null;
      } else {
        setStatus("Validando conexión...");
      }
    } finally {
      setIsCheckingSpeed(false);
      checkingRef.current = false;
    }
  };

  useEffect(() => {
    checkConnection(true);

    const pingInterval = setInterval(() => {
      checkConnection(false);
    }, PING_INTERVAL_MS);

    const speedInterval = setInterval(() => {
      checkConnection(true);
    }, SPEED_INTERVAL_MS);

    const handleOnline = () => {
      setIsOnline(true);
      checkConnection(true);
    };

    const handleOffline = () => {
      failCountRef.current = 0;
      setIsOnline(false);
      setStatus("Sin conexión");
      setLatency(null);
      setSpeedMbps(null);
      speedRef.current = null;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      clearInterval(pingInterval);
      clearInterval(speedInterval);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const getColor = () => {
    if (!isOnline) return "#dc3545";
    if (status === "Conexión estable") return "#28a745";
    if (status === "Conexión lenta") return "#ffc107";
    if (status === "Señal muy débil") return "#fd7e14";
    if (status === "Validando conexión...") return "#6c757d";
    return "#6c757d";
  };

  const getTitle = () => {
    if (!isOnline) return status;

    const parts = [];

    if (latency !== null) {
      parts.push(`Latencia: ${latency} ms`);
    }

    if (speedMbps !== null) {
      parts.push(`Descarga aproximada: ${speedMbps} Mbps`);
    }

    return parts.length > 0 ? parts.join(" | ") : status;
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "14px",
        right: "14px",
        background: "#fff",
        border: `1px solid ${getColor()}`,
        borderRadius: "20px",
        padding: "5px 10px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        zIndex: 99999,
        fontSize: "11px",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        lineHeight: "1",
      }}
      title={getTitle()}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          backgroundColor: getColor(),
          display: "inline-block",
          flexShrink: 0,
        }}
      />

      <span
        style={{
          fontWeight: "600",
          color: getColor(),
          whiteSpace: "nowrap",
        }}
      >
        {isCheckingSpeed ? "Midiendo conexión..." : status}
      </span>

      {latency !== null && (
        <span
          style={{
            color: "#666",
            whiteSpace: "nowrap",
          }}
        >
          {latency} ms
        </span>
      )}

      {speedMbps !== null && (
        <span
          style={{
            color: "#666",
            whiteSpace: "nowrap",
          }}
        >
          {speedMbps} Mbps
        </span>
      )}
    </div>
  );
};

export default InternetStatus;
